import { Injectable, Signal, computed, effect, signal } from '@angular/core';
import { Observable, Subject, Subscription, map, switchMap, tap, throwError, timer } from 'rxjs';
import { ControlService } from '../../library/control.service';
import { RealtimeService } from '../../library/realtime/realtime.service';
import { CallStoreService } from '../../library/call-store/call-store.service';
import { CallService } from 'src/app/services/learner/call.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { PathwayConstants } from 'src/app/utils/constants/pathway/pathway.constants';
import { AppConstants } from 'src/app/app-constants';
import { IInitiatePathwayStepSimulationResponse } from 'src/app/interfaces/call/iinitiate-pathway-step-simulation';
import { ICallRealtime } from 'src/app/interfaces/icall-realtime';
import { ICompleteDebriefRequest, ICompleteDebriefResponse, IPathwayNextStepResponse, IPathwayRunStepResponse, IRunDebriefResponse } from 'src/app/interfaces/ipathway-enrollment';
import { AssignmentService } from 'src/app/services/learner/assignment.service';
import { ICallDetailModal } from 'src/app/interfaces/icall-detail-modal';
import { RoleplayFooterState, isRoleplayStepType } from 'src/app/utils/enums/learner/pathway.enum';
import { PlayerService } from '../../library/player.service';
import { PathwaysService } from 'src/app/services/learner/pathways.service';

@Injectable()
export class PathwayRoleplayService {
  mainIsLoading = signal(false);
  isPreamble = signal(false);
  callDetails = signal<ICallDetailModal | null>(null);
  readonly footerState = signal<RoleplayFooterState>(
    RoleplayFooterState.Preamble,
  );
  readonly hasFailed = signal<boolean>(false);
  readonly canProceedFromPreamble = signal<boolean>(false);
  readonly isDebriefStarting = signal(false);
  readonly isPrimaryActionDisabled = computed(() => {
    if (this.mainIsLoading()) return true;
    if (this.isResolvingNextStep()) return true;
    const state = this.footerState();
    if (state === RoleplayFooterState.Preamble) return true;
    if (state === RoleplayFooterState.Debrief) return this.isDebriefStarting();
    if (state === RoleplayFooterState.End)
      return this.lastCallStore?.getDisableEndCallButton?.() ?? false;
    return false;
  });
  readonly isPostCallVisible = signal(false);
  readonly captionText = signal<string>('');
  readonly practiceCallUuid = signal<string>('');
  readonly callType = signal<string>('');
  readonly debriefInstanceUuid = signal<string>('');
  readonly debriefFocusedOpportunityTitle = signal<string>('');
  readonly debriefStrategiesDiscussed = signal<string[]>([]);
  readonly nextStepResponse = signal<IPathwayNextStepResponse | null>(null);
  readonly isResolvingNextStep = signal(false);
  readonly cachedDebriefResponse = signal<ICompleteDebriefResponse | null>(
    null,
  );

  readonly onNavigateHome$ = new Subject<void>();

  private subscriptions: Subscription[] = [];
  private enrollmentUuid: string = '';
  private lastRunStep: IPathwayRunStepResponse | null = null;
  private lastModuleAssignmentUuid: string = '';
  private lastRealtimeService: RealtimeService | undefined;
  private lastControlService: ControlService | undefined;
  private lastCallStore: CallStoreService | undefined;
  private preambleAutoCompleted = false;
  private debriefInteractionInterval: ReturnType<typeof setInterval> | null =
    null;
  private readonly DEBRIEF_INTERACTION_THRESHOLD = 4;

  get lastRoleplayStepUuid(): string {
    return this.lastRunStep?.stepUuid ?? '';
  }

  setFooterState(state: RoleplayFooterState): void {
    this.footerState.set(state);
  }

  constructor(
    private readonly callService: CallService,
    private readonly loggerService: LoggerService,
    private readonly assignmentService: AssignmentService,
    private readonly playerService: PlayerService,
    private readonly pathwaysService: PathwaysService,
  ) {
    effect(() => {
      const speech = this.playerService.avatarSpeech();
      if (speech) this.captionText.set(speech);
    });

    effect(() => {
      if (
        this.canProceedFromPreamble() &&
        this.footerState() === RoleplayFooterState.Preamble
      ) {
        this.footerState.set(RoleplayFooterState.PreambleActive);
      }
    });

    effect(() => {
      if (this.debriefInstanceUuid()) {
        this.startDebriefInteractionMonitoring();
      } else {
        this.stopDebriefInteractionMonitoring();
      }
    });
  }

  init(params: {
    moduleAssignmentUuid: string;
    enrollmentStepUuid: string;
    enrollmentUuid: string;
    stepType: string;
    runStepResponse: Signal<IPathwayRunStepResponse | null> | undefined;
    realtimeService: RealtimeService | undefined;
    controlService: ControlService | undefined;
    callStore: CallStoreService | undefined;
    startDebrief?: boolean;
  }): void {
    const runStep = params.runStepResponse?.();
    this.lastRunStep = runStep ?? (params.enrollmentStepUuid ? { stepUuid: params.enrollmentStepUuid } as IPathwayRunStepResponse : null);
    this.enrollmentUuid = params.enrollmentUuid ?? '';
    this.lastModuleAssignmentUuid = params.moduleAssignmentUuid ?? '';
    this.lastRealtimeService = params.realtimeService;
    this.lastControlService = params.controlService;
    this.lastCallStore = params.callStore;

    if (params.startDebrief) return;

    const isPreamble = runStep?.isPreamble ?? false;
    const enrollmentStepUuid =
      runStep?.stepUuid ?? params.enrollmentStepUuid ?? '';

    if (isPreamble) {
      this.isPreamble.set(true);
      this.footerState.set(RoleplayFooterState.Preamble);
      this.canProceedFromPreamble.set(false);
      this.preambleAutoCompleted = false;

      // Roleplay preamble has no user video: ensure camera is off.
      if (params.controlService?.isVideoOn) {
        params.controlService.toggleVideo();
      }
    } else {
      this.footerState.set(RoleplayFooterState.End);
    }

    if (isPreamble && isRoleplayStepType(params.stepType)) {
      const callUuid = runStep?.callUuid ?? '';
      this.startPreambleInteraction({
        ...params,
        callUuid,
        enrollmentStepUuid: enrollmentStepUuid,
      });
    }

    if (isRoleplayStepType(params.stepType) && !isPreamble) {
      const callUuid = runStep?.callUuid ?? '';
      const callType = runStep?.callType ?? '';

      this.initiateAndStartCall({
        ...params,
        callUuid,
        callType,
        enrollmentStepUuid: enrollmentStepUuid,
      });
    }

    this.fetchCallDetails({
      stepType: params.stepType,
      callUuid: isPreamble
        ? (runStep?.stepCallUuid ?? '')
        : (runStep?.callUuid ?? ''),
      moduleAssignmentUuid: params.moduleAssignmentUuid,
    });
  }

  launchSimulationFromPreamble(): void {
    if (!this.enrollmentUuid) {
      this.loggerService.error(
        '[ROLEPLAY] launchSimulationFromPreamble: missing enrollmentUuid',
      );
      return;
    }
    if (!this.lastRunStep?.stepUuid) {
      this.loggerService.error(
        '[ROLEPLAY] launchSimulationFromPreamble: missing stepUuid',
      );
      return;
    }
    if (!this.lastModuleAssignmentUuid) {
      this.loggerService.error(
        '[ROLEPLAY] launchSimulationFromPreamble: missing moduleAssignmentUuid',
      );
      return;
    }
    if (
      !this.lastRealtimeService ||
      !this.lastControlService ||
      !this.lastCallStore
    ) {
      this.loggerService.error(
        '[ROLEPLAY] launchSimulationFromPreamble: missing required services',
      );
      return;
    }

    this.mainIsLoading.set(true);

    try {
      this.loggerService.log('[ROLEPLAY] launchSimulationFromPreamble - calling endRealtimeConversation');
      this.lastRealtimeService.endRealtimeConversation(true, true);
    } catch (err) {
      this.loggerService.error(
        '[ROLEPLAY] launchSimulationFromPreamble: endRealtimeConversation failed',
        err,
      );
    }

    const runMainStep = () => {
      this.subscriptions.push(
        this.pathwaysService
          .runStepByEnrollmentUuid(
            this.enrollmentUuid,
            this.lastRunStep!.stepUuid!,
          )
          .subscribe({
            next: (runResponse: IPathwayRunStepResponse) => {
              this.lastRunStep = runResponse;

              if (runResponse.status === PathwayConstants.API_STATUS_BLOCKED) {
                this.onNavigateHome$.next();
                this.mainIsLoading.set(false);
                return;
              }

              if (!runResponse.callUuid) {
                this.loggerService.error(
                  '[ROLEPLAY] launchSimulationFromPreamble: runStep returned missing callUuid',
                );
                this.mainIsLoading.set(false);
                return;
              }

              if (runResponse.isPreamble) {
                this.preambleAutoCompleted = false;
                this.canProceedFromPreamble.set(false);
                this.isPreamble.set(true);
                this.footerState.set(RoleplayFooterState.Preamble);
                this.startPreambleInteraction({
                  moduleAssignmentUuid: this.lastModuleAssignmentUuid,
                  callUuid: runResponse.callUuid,
                  enrollmentStepUuid: runResponse.stepUuid ?? '',
                  realtimeService: this.lastRealtimeService,
                  controlService: this.lastControlService,
                  callStore: this.lastCallStore,
                });
                this.mainIsLoading.set(false);
                return;
              }

              this.isPreamble.set(false);
              this.footerState.set(RoleplayFooterState.End);

              this.initiateAndStartCall({
                moduleAssignmentUuid: this.lastModuleAssignmentUuid,
                callUuid: runResponse.callUuid,
                callType: runResponse.callType ?? '',
                enrollmentStepUuid: runResponse.stepUuid ?? '',
                realtimeService: this.lastRealtimeService,
                controlService: this.lastControlService,
                callStore: this.lastCallStore,
              });
              this.mainIsLoading.set(false);
            },
            error: (err) => {
              this.loggerService.error(
                '[ROLEPLAY] launchSimulationFromPreamble: runStep failed',
                err,
              );
              this.mainIsLoading.set(false);
            },
          }),
      );
    };

    if (this.preambleAutoCompleted) {
      runMainStep();
      return;
    }

    this.subscriptions.push(
      this.pathwaysService
        .completeStepByEnrollmentUuid(this.enrollmentUuid, this.lastRunStep!.stepUuid!)
        .subscribe({
          next: () => runMainStep(),
          error: (err) => {
            this.loggerService.error(
              '[ROLEPLAY] launchSimulationFromPreamble: completeStep failed',
              err,
            );
            this.mainIsLoading.set(false);
          },
        }),
    );
  }

  fetchCallDetails(params: {
    stepType: string;
    callUuid: string;
    moduleAssignmentUuid: string;
  }): void {
    if (this.callDetails()) {
      return;
    }

    if (!isRoleplayStepType(params.stepType)) {
      return;
    }

    if (!params.callUuid || !params.moduleAssignmentUuid) {
      this.loggerService.error(
        '[ROLEPLAY] fetchCallDetails: missing callUuid or moduleAssignmentUuid',
      );
      return;
    }

    this.subscriptions.push(
      this.assignmentService
        .getCallDetails(params.callUuid, params.moduleAssignmentUuid)
        .subscribe({
          next: (response: ICallDetailModal) => {
            this.callDetails.set(response);
          },
          error: (error) => {
            this.loggerService.error(
              '[ROLEPLAY] fetchCallDetails: failed to load call details',
              error,
            );
          },
        }),
    );
  }

  private initiateAndStartCall(params: {
    moduleAssignmentUuid: string;
    callUuid: string;
    callType: string;
    enrollmentStepUuid: string;
    realtimeService: RealtimeService | undefined;
    controlService: ControlService | undefined;
    callStore: CallStoreService | undefined;
  }): void {
    const { moduleAssignmentUuid, callUuid, callType, enrollmentStepUuid } =
      params;
    this.callType.set(callType);

    if (!moduleAssignmentUuid || !callUuid || !enrollmentStepUuid) {
      this.loggerService.error(
        '[ROLEPLAY] Missing moduleAssignmentUuid, callUuid or enrollmentStepUuid',
      );
      return;
    }

    this.mainIsLoading.set(true);

    this.subscriptions.push(
      this.callService
        .initiatePathwayStepSimulation(
          moduleAssignmentUuid,
          callUuid,
          PathwayConstants.PRACTICE_TYPE_PATHWAY,
          callType,
          enrollmentStepUuid,
        )
        .subscribe({
          next: async (res: IInitiatePathwayStepSimulationResponse) => {
            try {
              if (!params.realtimeService) {
                this.loggerService.error('[ROLEPLAY] Missing realtimeService');
                this.mainIsLoading.set(false);
                return;
              }

              const conversationId = res.conversationId ?? '';
              const conversationUrl = res.conversationUrl ?? '';

              if (!conversationId || !conversationUrl) {
                this.loggerService.error(
                  '[ROLEPLAY] initiatePathwayStepSimulation response missing conversation details',
                );
                this.mainIsLoading.set(false);
                return;
              }

              if (params.controlService && params.callStore) {
                params.controlService.setQueryParams(
                  moduleAssignmentUuid,
                  callUuid,
                  PathwayConstants.PRACTICE_TYPE_PATHWAY,
                  callType,
                  undefined,
                );
                params.callStore.setCallUuid(callUuid);
                params.callStore.setCallType(callType);
                params.callStore.setPracticeCallUuid(res.practiceCallUuid);
                this.practiceCallUuid.set(res.practiceCallUuid);
                params.callStore.setCurrentView(AppConstants.VIEW_PRACTICE);
                params.callStore.setDisplayPostCall(false);
                params.callStore.setIsMicOn(
                  params.callStore.getUserMicPreference(),
                );
              }

              const realtimePayload: ICallRealtime = {
                practiceCallUuid: res.practiceCallUuid,
                conversationId,
                conversationUrl,
                callName: res.callName ?? '',
                avatarName: res.avatarName ?? '',
                pronounceWords: res.pronounceWords ?? [],
                resumeMessage: res.resumeMessage ?? [],
                visualAid: res.visualAid ?? undefined,
                customBackgroundUrl: res.customBackgroundUrl ?? undefined,
                hideResultsForLearners: res.hideResultsForLearners ?? null,
              };

              // false = conversational, not guided
              await params.realtimeService.initializeFromStartConversationResponse(
                realtimePayload,
                false,
              );

              params.realtimeService.conversationInitializedByPathwayFlow = true;

              params.callStore?.setCurrentView(AppConstants.VIEW_PRACTICE);

              this.mainIsLoading.set(false);
            } catch (error) {
              this.loggerService.error(
                '[ROLEPLAY] initiateAndStartCall failed',
                error,
              );
              this.mainIsLoading.set(false);
            }
          },
          error: (err) => {
            this.loggerService.error(
              '[ROLEPLAY] initiatePathwayStepSimulation error',
              err,
            );
            this.mainIsLoading.set(false);
          },
        }),
    );
  }

  private startPreambleInteraction(params: {
    moduleAssignmentUuid: string;
    callUuid: string;
    enrollmentStepUuid: string;
    realtimeService: RealtimeService | undefined;
    controlService: ControlService | undefined;
    callStore: CallStoreService | undefined;
  }): void {
    if (
      !params.realtimeService ||
      !params.controlService ||
      !params.callStore
    ) {
      this.loggerService.error(
        '[ROLEPLAY] startPreambleInteraction: missing required services',
      );
      return;
    }

    if (
      !params.callUuid ||
      !params.moduleAssignmentUuid ||
      !params.enrollmentStepUuid
    ) {
      this.loggerService.error(
        '[ROLEPLAY] startPreambleInteraction: missing callUuid, moduleAssignmentUuid or enrollmentStepUuid',
      );
      return;
    }

    params.controlService.setQueryParams(
      params.moduleAssignmentUuid,
      params.callUuid,
      PathwayConstants.PRACTICE_TYPE_PATHWAY,
      AppConstants.CALL_TYPE_FRAMING,
      undefined,
    );
    params.callStore.setCallUuid(params.callUuid);
    params.callStore.setCallType(AppConstants.CALL_TYPE_FRAMING);

    params.realtimeService
      .startFramingCallRequest(params.enrollmentStepUuid)
      .then(() => {})
      .catch((error) => {
        this.loggerService.error(
          '[ROLEPLAY] startPreambleInteraction: framing call failed',
          error,
        );
      });

    this.subscriptions.push(
      params.realtimeService.welcomeMessageSpoken$.subscribe(() => {
        if (!this.isPreamble() || this.preambleAutoCompleted) return;
        this.preambleAutoCompleted = true;
        this.pathwaysService
          .completeStepByEnrollmentUuid(this.enrollmentUuid, params.enrollmentStepUuid)
          .subscribe({
            next: () => {
              this.canProceedFromPreamble.set(true);
            },
            error: (err) => {
              this.loggerService.error(
                '[ROLEPLAY] preamble completeStepOnly failed',
                err,
              );
            },
          });
      }),
    );
  }

  runDebiefAndStartGetRealTimeCallInterface(): Observable<ICallRealtime> {
    const {
      enrollmentUuid,
      lastModuleAssignmentUuid: moduleAssignmentUuid,
      lastRunStep,
    } = this;
    const stepUuid = lastRunStep?.stepUuid ?? '';

    if (!enrollmentUuid || !stepUuid || !moduleAssignmentUuid) {
      this.loggerService.error(
        '[ROLEPLAY] runDebiefAndStartGetRealTimeCallInterface: missing enrollmentUuid, stepUuid or moduleAssignmentUuid',
      );
      return throwError(
        () =>
          new Error(
            'runDebiefAndStartGetRealTimeCallInterface: missing required params',
          ),
      );
    }

    return this.pathwaysService.runDebrief(enrollmentUuid, stepUuid).pipe(
      tap((response: IRunDebriefResponse) => {
        this.debriefInstanceUuid.set(response.debriefInstanceUuid ?? '');
      }),
      switchMap((response: IRunDebriefResponse) => {
        const callUuid = response.callUuid ?? '';
        const callType = response.callType ?? '';

        if (response.status !== 'STARTED' || !callUuid) {
          this.loggerService.error(
            '[ROLEPLAY] runDebiefAndStartGetRealTimeCallInterface: runDebrief blocked or missing callUuid',
            response,
          );
          return throwError(
            () => new Error(`runDebrief: ${response.reason ?? 'BLOCKED'}`),
          );
        }

        return this.callService
          .initiatePathwayStepSimulation(
            moduleAssignmentUuid,
            callUuid,
            PathwayConstants.PRACTICE_TYPE_PATHWAY,
            callType,
            stepUuid,
            response.debriefInstanceUuid ?? undefined,
          )
          .pipe(
            map(
              (res: IInitiatePathwayStepSimulationResponse): ICallRealtime => ({
                practiceCallUuid: res.practiceCallUuid,
                conversationId: res.conversationId ?? '',
                conversationUrl: res.conversationUrl ?? '',
                callName: res.callName ?? '',
                avatarName: res.avatarName ?? '',
                pronounceWords: res.pronounceWords ?? [],
                resumeMessage: res.resumeMessage ?? [],
                visualAid: res.visualAid ?? undefined,
                customBackgroundUrl: res.customBackgroundUrl ?? undefined,
                hideResultsForLearners: res.hideResultsForLearners ?? null,
              }),
            ),
          );
      }),
    );
  }

  private startDebriefInteractionMonitoring(): void {
    if (this.debriefInteractionInterval) return;
    this.debriefInteractionInterval = setInterval(() => {
      const count = this.lastControlService?.interactionCount ?? 0;
      if (
        count >= this.DEBRIEF_INTERACTION_THRESHOLD &&
        !this.cachedDebriefResponse()
      ) {
        this.stopDebriefInteractionMonitoring();
        this.completeDebrief().subscribe({
          next: (response) => this.cachedDebriefResponse.set(response),
          error: (err) =>
            this.loggerService.error(
              '[ROLEPLAY] auto completeDebrief failed',
              err,
            ),
        });
      }
    }, 500);
  }

  private stopDebriefInteractionMonitoring(): void {
    if (this.debriefInteractionInterval) {
      clearInterval(this.debriefInteractionInterval);
      this.debriefInteractionInterval = null;
    }
  }

  completeDebrief(): Observable<ICompleteDebriefResponse> {
    const request: ICompleteDebriefRequest = {
      debriefInstanceUuid: this.debriefInstanceUuid(),
      exchangeCount: this.lastControlService?.interactionCount ?? 0,
      focusedOpportunityTitle: this.debriefFocusedOpportunityTitle(),
      strategiesDiscussed: this.debriefStrategiesDiscussed(),
    };
    return this.pathwaysService.completeDebrief(request);
  }

  resolvePostCallFooterState(hasDebrief: boolean, passed: boolean): void {
    this.isResolvingNextStep.set(true);
    if (!passed && hasDebrief) this.footerState.set(RoleplayFooterState.Debrief);
    else if (!passed) this.footerState.set(RoleplayFooterState.RetryRoleplay);

    this.subscriptions.push(
      timer(3000)
        .pipe(
          switchMap(() =>
            this.pathwaysService.getNextStepByEnrollmentUuid(
              this.enrollmentUuid,
            ),
          ),
        )
        .subscribe({
          next: (response: IPathwayNextStepResponse) => {
            this.nextStepResponse.set(response);

            const isSameStepRedirect = this.isSameStepRedirect(response);
            const effectivelyFailed = !passed || isSameStepRedirect;
            const isBlocked = response.status === PathwayConstants.API_STATUS_BLOCKED;
            this.hasFailed.set(effectivelyFailed);

            if (effectivelyFailed && hasDebrief) {
              this.footerState.set(RoleplayFooterState.Debrief);
            } else if (effectivelyFailed && !isBlocked) {
              this.footerState.set(RoleplayFooterState.RetryRoleplay);
            } else if (isBlocked) {
              this.footerState.set(RoleplayFooterState.ExitPathway);
            } else if (response.toStepUuid) {
              this.footerState.set(RoleplayFooterState.MoveToNext);
            } else {
              this.footerState.set(RoleplayFooterState.ExitPathway);
            }
            this.isResolvingNextStep.set(false);
          },

          error: (err) => {
            this.loggerService.error(
              '[ROLEPLAY] resolvePostCallFooterState failed',
              err,
            );
            this.footerState.set(RoleplayFooterState.ExitPathway);
            this.isResolvingNextStep.set(false);
          },
        }),
    );
  }

  private isSameStepRedirect(response: IPathwayNextStepResponse): boolean {
    return (
      response.status === PathwayConstants.API_STATUS_REDIRECTED &&
      response.toStepUuid != null &&
      response.toStepUuid === (response.fromStepUuid ?? this.lastRunStep?.stepUuid)
    );
  }

  prepareForNextStep(): void {
    try {
      this.loggerService.log('[ROLEPLAY] prepareForNextStep - calling endRealtimeConversation');
      this.lastRealtimeService?.endRealtimeConversation(true, true);
    } catch (err) {
      this.loggerService.error(
        '[ROLEPLAY] prepareForNextStep: endRealtimeConversation failed',
        err,
      );
    }

    if (this.lastControlService) {
      this.lastControlService.interactionCount = 0;
    }

    this.lastCallStore?.setDisplayPostCall(false);
    this.lastCallStore?.setIsCallComplete(false);
    this.lastCallStore?.setCurrentView('');
    this.lastCallStore?.setCallType('');

    this.stopDebriefInteractionMonitoring();
    this.isPostCallVisible.set(false);
    this.debriefInstanceUuid.set('');
    this.debriefFocusedOpportunityTitle.set('');
    this.debriefStrategiesDiscussed.set([]);
    this.nextStepResponse.set(null);
    this.cachedDebriefResponse.set(null);
  }

  destroy(): void {
    this.stopDebriefInteractionMonitoring();
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
    this.onNavigateHome$.complete();
  }
}
